import { SearchProductsQueryVariables } from "../../../common/generated-types";
import { MOV_FILTER_OPTIONS } from "../../../shared/constants/pricelist.constants";
import { MovFilterOption } from "../../../shared/types/pricelist.types";
import { normalizeEanSearchTerm } from "../../../shared/utils/parse-ean-list";
import { FACET_SEARCH_ROUTE_QUERY_PARAM } from "../product-list-controls/product-list-controls.constants";
import { MOV_FILTER_QUERY_PARAM } from "../mov-filter/mov-filter.constants";
import { SUPPLY_TYPE_FILTER_QUERY_PARAM } from "../supply-type-filter/supply-type-filter.constants";
import {
    LEGACY_SEARCH_QUERY_PARAM,
    PAGE_QUERY_PARAM,
    SHIPPING_DATE_FILTER_QUERY_PARAM,
} from "./product-list.constants";
import { formatLeadTimeWeeksLabel } from "../../../shared/utils/lead-time-weeks.util";
import {
    MovRangeFilter,
    ProductListCollection,
    ShippingDateRangeFilter,
    SupplierPartialInfo,
} from "./product-list.types";

export const PROGRESS_COLOR_READY = "#22c55e";
export const PROGRESS_COLOR_WARNING = "#facc15";
export const PROGRESS_COLOR_LOW = "#ef4444";

export function isSupplierPreOrder(
    supplierInfo?: SupplierPartialInfo | null
): boolean {
    return supplierInfo?.supplyType?.toLowerCase() === "pre-order";
}

export function getSupplierDeliveryTerm(
    supplierInfo?: SupplierPartialInfo | null
): string | null {
    if (!supplierInfo || !isSupplierPreOrder(supplierInfo)) {
        return null;
    }
    const s = supplierInfo;
    if (s.preOrderShippingDate) {
        return String(s.preOrderShippingDate).trim();
    }
    const label = formatLeadTimeWeeksLabel(s.leadTimeFrom, s.leadTimeTo);
    return label === "—" ? null : label;
}

export function showSupplierPrePayment(
    supplierInfo?: SupplierPartialInfo | null
): boolean {
    if (!isSupplierPreOrder(supplierInfo) || !supplierInfo) {
        return false;
    }
    const p = supplierInfo.prePaymentPercentage;
    return (
        p !== undefined &&
        p !== null &&
        !Number.isNaN(Number(p))
    );
}

export function getRemainingToMinimumOrder(
    supplierInfo: SupplierPartialInfo | null,
    supplierSpecificTotal: number
): number {
    if (!supplierInfo || !supplierInfo.mov) {
        return 0;
    }

    const movInCents = supplierInfo.mov;
    return Math.max(0, movInCents - supplierSpecificTotal);
}

export function isMinimumOrderMet(
    supplierInfo: SupplierPartialInfo | null,
    supplierSpecificTotal: number
): boolean {
    if (!supplierInfo || !supplierInfo.mov) {
        return true;
    }
    const movInCents = supplierInfo.mov;
    return supplierSpecificTotal >= movInCents;
}

export function getProgressPercentage(
    supplierInfo: SupplierPartialInfo | null,
    supplierSpecificTotal: number
): number {
    if (!supplierInfo || !supplierInfo.mov || supplierInfo.mov <= 0) {
        return 100;
    }

    const movInCents = supplierInfo.mov;
    return Math.min((supplierSpecificTotal / movInCents) * 100, 100);
}

export function getProgressColor(percentage: number): string {
    if (percentage >= 100) {
        return PROGRESS_COLOR_READY;
    }
    if (percentage >= 50) {
        return PROGRESS_COLOR_WARNING;
    }
    return PROGRESS_COLOR_LOW;
}

export function transformShippingDateRanges(
    ranges: string[]
): ShippingDateRangeFilter[] {
    return ranges.map((range) => {
        const weekMatch = range.match(/^up-to-(\d+)$/);
        if (weekMatch) {
            const weeks = parseInt(weekMatch[1], 10);
            return { minDays: 0, maxDays: weeks * 7 };
        }

        switch (range) {
            case "up-to-2":
                return { minDays: 0, maxDays: 14 };
            case "up-to-4":
                return { minDays: 0, maxDays: 28 };
            case "up-to-6":
                return { minDays: 0, maxDays: 42 };
            case ">6":
                return { minDays: 42 };
            default:
                return { minDays: 0, maxDays: 14 };
        }
    });
}

export function transformMovRanges(
    ranges: string[],
    toEuros = false
): MovRangeFilter[] {
    const options = MOV_FILTER_OPTIONS;
    const result: MovRangeFilter[] = [];
    (ranges || []).forEach((id) => {
        const opt = options.find(
            (o: MovFilterOption) => o.id === id || o.id === `mov${id}`
        );
        if (opt) {
            result.push({
                min: toEuros ? opt.min / 100 : opt.min,
                max: toEuros ? opt.max / 100 : opt.max,
            });
        }
    });
    return result;
}

export function getSearchInput(
    term: string,
    collection: ProductListCollection,
    facetValueFilters: { or: string[] }[],
    supplierId: string | null,
    perPage: number,
    currentPage: number,
    shippingDateRanges?: string[],
    movRanges?: string[],
    sortField?: string | null,
    sortOrder?: "ASC" | "DESC" | null,
    supplyTypes?: string[],
    supplierIds?: number[],
): SearchProductsQueryVariables {
    const baseInput = {
        groupByProduct: true,
        collectionId: collection?.id,
        facetValueFilters,
        take: perPage,
        skip: (currentPage - 1) * perPage,
        ...(supplierId ? { supplierId: parseInt(supplierId, 10) } : {}),
        ...(supplierIds ? { supplierIds } : {}),
        ...(shippingDateRanges && shippingDateRanges.length > 0
            ? {
                  shippingDateRanges:
                      transformShippingDateRanges(shippingDateRanges),
              }
            : {}),
        ...(movRanges && movRanges.length > 0
            ? { movRanges: transformMovRanges(movRanges, true) }
            : {}),
        ...(supplyTypes && supplyTypes.length > 0 ? { supplyTypes } : {}),
    };

    return {
        input: {
            ...baseInput,
            sort:
                sortField && sortOrder
                    ? { [sortField]: sortOrder }
                    : undefined,
            term: normalizeEanSearchTerm(term),
        },
    } as SearchProductsQueryVariables;
}

export function buildProductListRouteCommands(
    routerUrl: string,
    facetIds: string[] = [],
): (string | { facets: string[] })[] {
    const basePath = routerUrl.split(";")[0].split("?")[0];
    if (facetIds.length === 0) {
        return [basePath];
    }
    return [basePath, { facets: facetIds }];
}

export function getClearProductListFilterQueryParams(): Record<
    string,
    null | number
> {
    return {
        [SHIPPING_DATE_FILTER_QUERY_PARAM]: null,
        [MOV_FILTER_QUERY_PARAM]: null,
        [SUPPLY_TYPE_FILTER_QUERY_PARAM]: null,
        [LEGACY_SEARCH_QUERY_PARAM]: null,
        [FACET_SEARCH_ROUTE_QUERY_PARAM]: null,
        [PAGE_QUERY_PARAM]: 1,
    };
}
